import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { isDesktop } from '@/lib/desktop';
import { getSupabase, hasSupabaseConfig } from '@/lib/auth/supabase';

const DEBUG_LOG_KEY = 'doneline-deep-link-log';
const DEBUG_LOG_MAX = 20;

/**
 * Tauri 桌面壳里的 magic-link 回调处理。
 *
 * 流程：
 *  1. 登录时 SPA 让 Supabase 把 magic link 的 redirect 设成 `doneline://auth/callback`（见 session.ts）
 *  2. 用户点邮件链接 → macOS 把 doneline:// 路由到我们的 .app
 *  3. URL 形如：doneline://auth/callback#access_token=xxx&refresh_token=yyy&...
 *  4. 这里抠出 hash 里的 access_token / refresh_token，调 `supabase.auth.setSession`
 *  5. session.ts 的 onAuthStateChange 收到 SIGNED_IN，cached 更新，UI 自动跳转
 *
 * 关键：tauri-plugin-deep-link 有两个入口
 *  - `getCurrent()`: app 冷启动时被 doneline:// 唤起，URL 当作 launch argument 传进来
 *    → 必须主动取，否则收不到。第一次点 magic link（app 没在跑）走的是这条路。
 *  - `onOpenUrl(cb)`: app 已经在运行，用户又点了一次链接，URL 通过事件投递
 *
 * 调试：每一步都 toast.* + 写一条到 localStorage[doneline-deep-link-log]，
 * 出问题方便用户截图给我们或者自己在 DevTools 看。
 *
 * 安全：回调 URL 本体绝不落日志 —— hash 里是 access_token/refresh_token（长效凭证）、
 * query 里是未兑换的 PKCE code，写进 localStorage（长期驻留）或 Sentry breadcrumb
 * （sendDefaultPii 时随错误上传第三方）都等于泄漏。只记 path + 参数 key 名
 * （describeUrl），logEvent 内再对任意 payload 做一次 token/code 值脱敏兜底。
 */
export function useDeepLinkAuth() {
  useEffect(() => {
    if (!isDesktop() || !hasSupabaseConfig()) return;

    let unlisten: (() => void) | null = null;
    let cancelled = false;

    void (async () => {
      // 只在 main 窗口处理 magic-link 回调。桌面壳有第二个隐藏的 quickbar webview，与 main
      // 同源、共享 localStorage（含 PKCE code_verifier），两个 webview 都跑本 hook 会竞相
      // exchangeCodeForSession 同一个一次性 code → 输的那个弹「登录失败」toast（其实已登录成功）。
      // quickbar 只读已登录态显示今日待办，不需要参与认证。
      try {
        const { getCurrentWindow } = await import('@tauri-apps/api/window');
        const label = getCurrentWindow().label;
        if (label !== 'main') {
          logEvent('skip-non-main-window', { label });
          return;
        }
      } catch {
        // window API 不可用（单窗口 / 旧版）→ 放行，按单窗口处理，不阻断登录。
      }

      let onOpenUrl: typeof import('@tauri-apps/plugin-deep-link').onOpenUrl;
      let getCurrent: typeof import('@tauri-apps/plugin-deep-link').getCurrent;
      try {
        ({ onOpenUrl, getCurrent } = await import('@tauri-apps/plugin-deep-link'));
      } catch (err) {
        logEvent('plugin-load-failed', { error: stringifyError(err) });
        toast.error('Deep-link 插件加载失败，看控制台 / localStorage 拿日志');
        return;
      }

      // 冷启动路径：把这次启动 app 用的 URL 取出来过一遍
      try {
        const initial = await getCurrent();
        logEvent('cold-start', {
          count: initial?.length ?? 0,
          urls: (initial ?? []).map(describeUrl),
        });
        if (initial && !cancelled) {
          for (const url of initial) {
            await applyAuthCallback(url, 'cold-start');
          }
        }
      } catch (err) {
        logEvent('getCurrent-failed', { error: stringifyError(err) });
        toast.error(`getCurrent() 失败：${stringifyError(err)}`);
      }

      // 热路径：app 已经在跑，用户又点链接（少见但要支持）
      try {
        const off = await onOpenUrl((urls) => {
          logEvent('runtime', { count: urls.length, urls: urls.map(describeUrl) });
          for (const url of urls) {
            void applyAuthCallback(url, 'runtime');
          }
        });
        if (cancelled) {
          off();
          return;
        }
        unlisten = off;
      } catch (err) {
        logEvent('onOpenUrl-subscribe-failed', { error: stringifyError(err) });
        toast.error(`onOpenUrl 注册失败：${stringifyError(err)}`);
      }
    })();

    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, []);
}

async function applyAuthCallback(url: string, source: 'cold-start' | 'runtime'): Promise<void> {
  toast.info(`检测到登录回调（${source}），正在设置 session…`, { duration: 3000 });

  // Supabase JS SDK v2（@supabase/ssr 走 PKCE flow）的 magic link 回跳有两种格式：
  //  - PKCE 流程（默认）：doneline://auth/callback?code=xxx
  //    → 用 exchangeCodeForSession(code) 换 session
  //  - implicit 流程（老）：doneline://auth/callback#access_token=...&refresh_token=...
  //    → 用 setSession({ access_token, refresh_token })
  // 两条都接，先看 query.code，再看 hash 里的 access_token
  const queryParams = extractQueryParams(url);
  const hashParams = extractHashParams(url);

  // 错误码可能在 query 也可能在 hash
  const errorCode =
    queryParams.get('error') ?? hashParams.get('error') ?? hashParams.get('error_code');
  const errorDesc = queryParams.get('error_description') ?? hashParams.get('error_description');
  if (errorCode) {
    logEvent('supabase-error', { source, errorCode, errorDesc });
    toast.error(`Supabase 拒绝：${errorCode}${errorDesc ? ` — ${errorDesc}` : ''}`);
    return;
  }

  const code = queryParams.get('code');
  const accessToken = hashParams.get('access_token');
  const refreshToken = hashParams.get('refresh_token');

  if (code) {
    // PKCE 流程
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase.auth.exchangeCodeForSession(code);
      if (error) {
        logEvent('exchangeCode-error', { source, message: error.message, status: error.status });
        toast.error(`exchangeCodeForSession 失败：${error.message}`);
        return;
      }
      logEvent('exchangeCode-ok', { source, userId: data.session?.user.id ?? null });
      toast.success('登录成功！', { duration: 2000 });
      return;
    } catch (err) {
      logEvent('exchangeCode-throw', { source, error: stringifyError(err) });
      toast.error(`exchangeCodeForSession 异常：${stringifyError(err)}`);
      return;
    }
  }

  if (accessToken && refreshToken) {
    // implicit 流程
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      if (error) {
        logEvent('setSession-error', { source, message: error.message, status: error.status });
        toast.error(`setSession 失败：${error.message}`);
        return;
      }
      logEvent('setSession-ok', { source, userId: data.session?.user.id ?? null });
      toast.success('登录成功！', { duration: 2000 });
      return;
    } catch (err) {
      logEvent('setSession-throw', { source, error: stringifyError(err) });
      toast.error(`setSession 异常：${stringifyError(err)}`);
      return;
    }
  }

  // 两种都没拿到 → 报告所有看到的字段名（不带值，避免泄漏）
  const queryKeys = Array.from(queryParams.keys());
  const hashKeys = Array.from(hashParams.keys());
  logEvent('missing-tokens', { source, queryKeys, hashKeys });
  toast.error(
    `回调缺 token（query: ${queryKeys.join(',') || '空'} / hash: ${hashKeys.join(',') || '空'}）`,
  );
}

function extractQueryParams(url: string): URLSearchParams {
  const qIdx = url.indexOf('?');
  if (qIdx < 0) return new URLSearchParams();
  const tail = url.slice(qIdx + 1);
  const hashIdx = tail.indexOf('#');
  return new URLSearchParams(hashIdx < 0 ? tail : tail.slice(0, hashIdx));
}

function extractHashParams(url: string): URLSearchParams {
  const hashIdx = url.indexOf('#');
  if (hashIdx < 0) return new URLSearchParams();
  return new URLSearchParams(url.slice(hashIdx + 1));
}

/**
 * 回调 URL 的日志安全描述：只留 scheme+path 和 query/hash 的 key 名列表。
 * 值一律不进日志 —— refresh_token 是长效凭证、code 是未兑换的授权码。
 */
function describeUrl(url: string): string {
  const cutIdx = url.search(/[?#]/);
  const base = cutIdx < 0 ? url : url.slice(0, cutIdx);
  const queryKeys = Array.from(extractQueryParams(url).keys()).join(',');
  const hashKeys = Array.from(extractHashParams(url).keys()).join(',');
  return `${base} query:[${queryKeys}] hash:[${hashKeys}]`;
}

/**
 * 兜底脱敏：任何 `xxx_token=` / `code=` 形态的参数值一律遮蔽。防未来某个调用点
 * 又把原始 URL（或含 URL 的错误信息）塞进 payload。
 */
function redactTokenValues(text: string): string {
  return text.replace(/([a-z0-9_]*(?:token|code))=[^&\s"']+/gi, '$1=[redacted]');
}

function redactPayload(payload: Record<string, unknown>): Record<string, unknown> {
  try {
    return JSON.parse(redactTokenValues(JSON.stringify(payload))) as Record<string, unknown>;
  } catch {
    // stringify/parse 失败（循环引用等）→ 宁可丢明细也不冒泄漏风险
    return { redacted: true };
  }
}

function logEvent(kind: string, payload: Record<string, unknown>): void {
  // 所有出口（console / localStorage / Sentry）统一走脱敏后的 payload
  const safePayload = redactPayload(payload);
  const entry = { ts: new Date().toISOString(), kind, ...safePayload };

  console.warn('[deep-link-auth]', entry);
  try {
    const raw = window.localStorage.getItem(DEBUG_LOG_KEY);
    const arr: unknown[] = raw ? JSON.parse(raw) : [];
    arr.unshift(entry);
    window.localStorage.setItem(DEBUG_LOG_KEY, JSON.stringify(arr.slice(0, DEBUG_LOG_MAX)));
  } catch {
    /* localStorage 满 / 解析失败 — 不重要，console 已经记 */
  }
  // Sentry：每条事件都进 breadcrumb；失败类型升级成 message 报上去
  Sentry.addBreadcrumb({
    category: 'deep-link-auth',
    type: 'info',
    level: kind === 'setSession-ok' || kind === 'cold-start' ? 'info' : 'warning',
    message: kind,
    data: safePayload,
  });
  if (kind !== 'cold-start' && kind !== 'runtime' && kind !== 'setSession-ok') {
    Sentry.captureMessage(`deep-link-auth: ${kind}`, {
      level: 'warning',
      extra: safePayload,
    });
  }
}

function stringifyError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}
